import React, { JSX } from "react";
import { Box, Typography } from "@mui/material";
import { chain } from "mathjs";
import startCase from "lodash/startCase";
import "./PrintBill.scss";
import moment from "moment";
import {
  BillItemType,
  BillType,
  DiscountBasedOn,
  DocumentTypes
} from "../../../interfaces/BillInterfaces";
import { scaleFont } from "../../../components/Print/Print";
import { tl } from "../../../components/translate";
import classNames from "../../../helpers/classNames";
import { spFromIdSelector, spFullNameSelectorWithNmc } from "../../../reducers/serviceProvider";
import {
  BillColumns,
  getBillColumns,
  getColumnTextStyles,
  hasItemWithHSCode,
  marginForNameColumn,
  showInfoColumn,
  showUnitAfterQuantity
} from "../../../actions/helpers/billHelper";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import BillPrintSummary from "../BillPrintSummary";
import { getPaymentMethodLabel } from "../../Reports/SalesReport";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { useShowStockBatchAndExpiryInBillPrint } from "../../../hooks/useShowStockBatchAndExpiryInBillPrint";

interface Props {
  billData: BillType;
  SPs: ServiceProvider[];
  relatedSettings: { [key: string]: string };
  clientBalance: number;
  hidePaymentMethodOnPrint: boolean;
  user: { firstName: string; lastName: string };
}

const DescribeBillV2HalfLandScape = ({
  billData,
  SPs,
  relatedSettings,
  user,
  clientBalance,
  hidePaymentMethodOnPrint
}: Props): JSX.Element => {
  // custom hooks
  const currentRc = useCurrentResourceCentre();
  const { showStockBatchAndExpiryInBillPrint } = useShowStockBatchAndExpiryInBillPrint();

  if (!billData || !billData.document) return null;

  // variables
  const showInfo = showInfoColumn(billData);
  const showHSCode = hasItemWithHSCode(billData);

  const isvatResistered = currentRc?.settings?.billingSettings.vatRegistered;
  const showSubItemPriceOnPrint = currentRc?.settings?.billingSettings?.showSubItemPriceOnPrint;

  const billColumns = getBillColumns(showInfo, true).filter((header) => {
    if (header === BillColumns.hsCode) {
      return showHSCode;
    }

    if (header === BillColumns.DISCOUNT_AMT) {
      return !(
        [BillColumns.DISCOUNT_AMT].includes(header) &&
        billData.document?.settings.discountSettings.discountBasis === DiscountBasedOn.invoice
      );
    }
    if (header === BillColumns.VAT_PCT) {
      if (isvatResistered) {
        return header;
      }
      return false;
    }

    return header;
  });

  const showIndividualVAT =
    Object.keys(billData.document.summary.taxAmtWithRates || {}).filter(
      (vatPct) => Number(vatPct) !== 0
    ).length > 1;
  let netTotal;
  if (billData.document.type === DocumentTypes.CREDITNOTE) {
    netTotal = billData.document.billItems.reduce(
      (sum, item) =>
        chain(sum)
          .add(
            chain(item.perUnit || 0)
              .multiply(item.quantity || 1)
              .add(item.discountAmt || 0)
              .done()
          )
          .done(),
      0
    );
  } else {
    netTotal = billData.document.billItems.reduce(
      (sum, item) =>
        chain(sum)
          .add(
            chain(item.perUnit || 0)
              .multiply(item.quantity || 1)
              .subtract(item.discountAmt || 0)
              .done()
          )
          .done(),
      0
    );
  }

  const spField = (item: BillItemType) =>
    Array.isArray(item?.serviceProviderIds) && item.serviceProviderIds.length > 0 ? (
      <Box component="span" sx={{ ml: "4px", fontSize: "8px" }}>
        {item.serviceProviderIds
          .map((spId) => spFullNameSelectorWithNmc(spFromIdSelector(SPs, spId)))
          .join(", ")}
      </Box>
    ) : (
      // for older bills with single sp id
      item?.serviceProviderId && (
        <Box component="span" sx={{ ml: "4px", fontSize: "8px" }}>
          ({spFullNameSelectorWithNmc(spFromIdSelector(SPs, item.serviceProviderId))})
        </Box>
      )
    );

  return (
    <Box>
      <Box>
        <Box
          display="flex"
          borderRight={relatedSettings.isBordered ? "1px solid #bbb" : undefined}
          borderBottom={relatedSettings.isBordered ? "1px solid #bbb" : undefined}
          borderLeft={relatedSettings.isBordered ? "1px solid #bbb" : undefined}
          py="0.1cm"
          className={!relatedSettings.isBordered ? "borderBotBlack1" : undefined}
        >
          {billColumns.map((column, i) => (
            <Box
              key={column}
              className={classNames(
                [BillColumns.SNO, BillColumns.QUANTITY].includes(column) && "width1",
                [BillColumns.PER_UNIT].includes(column) && "flex1",

                [
                  BillColumns.hsCode,
                  BillColumns.DISCOUNT_AMT,
                  BillColumns.VAT_PCT,
                  BillColumns.GROSS_TOTAL,
                  BillColumns.SERVICE_PROVIDER
                ].includes(column) && "flex2",

                [BillColumns.DESCRIPTION, BillColumns.INFO].includes(column) && "flex3"
              )}
              sx={{
                mx: marginForNameColumn(column),
                px: i === 0 || i === billColumns.length - 1 ? "0.2cm" : ""
              }}
            >
              <Typography
                sx={{
                  fontSize: scaleFont("0.27cm", 0.85),
                  textTransform: "uppercase",
                  whiteSpace: "pre",
                  textAlign: getColumnTextStyles({ index: i, column }).textAlign,
                  fontWeight: 800
                }}
              >
                {tl(`billing.billItem.${column}`)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box component="div" paddingBottom="0.15cm">
          {[...billData.document.billItems]
            .sort(
              (a: Partial<BillItemType>, b: Partial<BillItemType>) => (a.sNo ?? 0) - (b.sNo ?? 0)
            )
            .map((item, index) => (
              <Box
                key={item.sNo}
                display="flex"
                sx={
                  relatedSettings.isBordered
                    ? {
                        pageBreakInside: "avoid"
                      }
                    : {}
                }
                className={
                  (index === billData.document.billItems.length - 1 &&
                    !relatedSettings.isBordered &&
                    "borderBotBlack1") ||
                  undefined
                }
              >
                {billColumns.map((column, i) => (
                  <Box
                    key={column}
                    className={classNames(
                      [BillColumns.SNO, BillColumns.QUANTITY].includes(column) && "width1",
                      [BillColumns.PER_UNIT].includes(column) && "flex1",

                      [
                        BillColumns.hsCode,
                        BillColumns.DISCOUNT_AMT,
                        BillColumns.VAT_PCT,
                        BillColumns.GROSS_TOTAL,
                        BillColumns.SERVICE_PROVIDER
                      ].includes(column) && "flex2",

                      [BillColumns.DESCRIPTION, BillColumns.INFO].includes(column) && "flex3"
                    )}
                    sx={{
                      px: i === 0 || i === billColumns.length - 1 ? "0.2cm" : "",
                      mx: marginForNameColumn(column)
                    }}
                  >
                    <Typography component="div">
                      <Box
                        sx={getColumnTextStyles({
                          index: i,
                          column,
                          fontSize: scaleFont("0.33cm", 0.85)
                        })}
                      >
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {["perUnit", "grossTotal"].includes(column) && item[column] !== null
                          ? Number(item[column]).toFixed(2)
                          : item[column]
                          ? item[column]
                          : "-"}

                        {column === BillColumns.SERVICE_PROVIDER && spField(item)}
                        {showUnitAfterQuantity(column) && item.unit}

                        {column === BillColumns.DESCRIPTION && (item?.teeth?.length ?? 0) > 0 && (
                          <Box component="span" sx={{ ml: "4px", fontSize: "10px" }}>
                            ({`Teeth: ${item?.teeth?.join(", ")}`})
                          </Box>
                        )}

                        {column === BillColumns.DESCRIPTION && (
                          <Box component="span" fontSize={scaleFont("0.23cm", 0.85)}>
                            {item.serviceCode && ` (${item.serviceCode})`}
                          </Box>
                        )}
                      </Box>

                      {column === "description" && showStockBatchAndExpiryInBillPrint && (
                        <Box
                          sx={{ whiteSpace: "pre-wrap", display: "inline" }}
                          fontSize={scaleFont("0.23cm", 0.85)}
                        >
                          {(item?.batchId || item.batchInfo?.batchId) &&
                            `Batch ${item?.batchId || item.batchInfo?.batchId}`}
                          {(item?.expiryDate || item.batchInfo?.expiryDate) &&
                            `, Expiring ${moment(
                              item?.expiryDate || item.batchInfo?.expiryDate
                            ).format("YYYY/MM/DD")}`}
                        </Box>
                      )}

                      {column === "description" &&
                        item?.subItems?.map((subitem) => (
                          <Box
                            key={subitem.productId}
                            paddingTop="0.05cm"
                            className="blockContent"
                            fontSize={scaleFont("0.23cm", 0.85)}
                          >
                            {`◦ ${subitem.description}`}
                            {spField(subitem)}
                          </Box>
                        ))}
                      {showSubItemPriceOnPrint &&
                        column === "perUnit" &&
                        item?.subItems?.map((subitem) => (
                          <Typography
                            key={subitem.productId}
                            textAlign="right"
                            paddingTop="0.05cm"
                            className="blockContent"
                            fontSize={scaleFont("0.23cm", 0.85)}
                          >
                            {`${subitem.productData?.grossTotalPrice}`}
                          </Typography>
                        ))}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
        </Box>
      </Box>
      <Box component="div" display="flex">
        <Box component="div" width="60%">
          {!hidePaymentMethodOnPrint &&
            (Boolean(billData?.paymentInfo?.paymentMethod) ||
              billData?.paymentInfo?.paymentBreakdown?.length >= 1) && (
              <Box display="flex" alignItems="flex-start" flex={1} gap={1}>
                <Typography fontSize={scaleFont("0.3cm", 0.85)} flexShrink={0}>
                  {tl("billing.paymentMethod")}:
                </Typography>
                <Typography fontSize={scaleFont("0.3cm", 0.85)} flexGrow={1} fontWeight={500}>
                  {startCase(
                    getPaymentMethodLabel({
                      paymentMethod: billData?.paymentInfo?.paymentMethod ?? null,
                      paymentBreakdown: billData.paymentInfo.paymentBreakdown ?? []
                    })
                  )}
                </Typography>
              </Box>
            )}
          <Box display="flex" alignItems="flex-start" gap={1}>
            <Typography fontSize={scaleFont("0.3cm", 0.85)} flexShrink={0}>
              {tl("billing.inWords")}:
            </Typography>
            <Typography fontSize={scaleFont("0.3cm", 0.85)} flexGrow={1} fontWeight={500}>
              {billData.document.summary.inWords} only
            </Typography>
          </Box>
          <Box mt={1}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Typography fontSize={scaleFont("0.3cm", 0.85)} flexShrink={0}>
                {tl("billing.printedDateTime")}:
              </Typography>
              <Typography fontSize={scaleFont("0.3cm", 0.85)} flexGrow={1} fontWeight={500}>
                {moment().format("YYYY/MM/DD")} {moment().format("h:mm a")}
              </Typography>
            </Box>
            {Boolean(billData?.printCount) && (
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Typography fontSize={scaleFont("0.3cm", 0.85)} flexShrink={0}>
                  {tl("billing.rePrintUser")}:
                </Typography>
                <Typography fontSize={scaleFont("0.3cm", 0.85)} flexGrow={1} fontWeight={500}>
                  {startCase(`${user?.firstName || ""} ${user?.lastName || ""}`)}
                </Typography>
              </Box>
            )}
            {billData.document.enteredBy.name && (
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Typography fontSize={scaleFont("0.3cm", 0.85)} flexShrink={0}>
                  {tl("billing.billedBy")}:
                </Typography>
                <Typography fontSize={scaleFont("0.3cm", 0.85)} flexGrow={1} fontWeight={500}>
                  {startCase(billData.document.enteredBy.name)}
                </Typography>
              </Box>
            )}
            {!billData.document.enteredBy?.signature && (
              <Box
                sx={{
                  height: 40,
                  width: 100,
                  borderBottom: "1px dashed black"
                }}
              />
            )}
          </Box>
        </Box>
        <Box width="40%">
          <Box pb="0.1cm" width="80%" ml="auto">
            <BillPrintSummary
              billData={billData}
              clientBalance={clientBalance}
              totalPerUnitTimesQty={billData.document.summary.totalPerUnitTimesQty}
              discount={billData.document.summary.discount}
              taxAmount={billData.document.summary.taxAmount}
              netTotal={netTotal}
              showIndividualVAT={showIndividualVAT}
              taxAmtWithRates={billData.document.summary.taxAmtWithRates}
              discountPercent={billData.document.summary.discountPct}
              smallFont
              noMarginTop
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DescribeBillV2HalfLandScape;
